import { useCallback, useEffect, useState } from 'react';
import { api, ApiError } from '../../lib/api';
import { useSession } from '../../lib/store';
import { Card, Empty, Notice, PageHeading, Spinner } from '../../components/Shell';
import { TextField } from '../../components/Field';
import { money } from '../../lib/format';
import type { TenantMetrics } from '../../lib/types';

const todayIso = () => new Date().toISOString().slice(0, 10);
const startOfMonthIso = () => `${todayIso().slice(0, 7)}-01`;

/**
 * Business reporting: what came in, what is still owed, and which service
 * earned it, for a date range the sitter picks. Nothing here is stored — the
 * API folds bookings and payments fresh on every request, so this is always
 * a read of the current state of the books, never a cached snapshot.
 */
export function AdminReports() {
  const session = useSession();
  const tenantId = session.tenant?.tenantId;

  const [from, setFrom] = useState(startOfMonthIso());
  const [to, setTo] = useState(todayIso());
  const [metrics, setMetrics] = useState<TenantMetrics | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    if (!tenantId) return;
    setLoading(true);
    try {
      const result = await api<TenantMetrics>(
        `/tenants/${tenantId}/metrics?from=${from}&to=${to}`
      );
      setMetrics(result);
      setError(null);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'The report could not be loaded.');
    } finally {
      setLoading(false);
    }
  }, [tenantId, from, to]);

  useEffect(() => {
    void load();
  }, [load]);

  const rangeInvalid = from > to;
  const topServiceEarnings = metrics?.byService[0]?.receivedCents ?? 0;

  return (
    <div className="mx-auto max-w-5xl px-5 pb-24 pt-12">
      <PageHeading
        eyebrow={session.tenant?.businessName}
        title="Reports"
        lead="Bookings and money received for a date range, aggregated fresh every time this page loads."
      />

      {error && (
        <div className="mb-8">
          <Notice tone="error">{error}</Notice>
        </div>
      )}

      <Card className="mb-10 p-5">
        <form
          className="flex flex-wrap items-end gap-4"
          onSubmit={(event) => {
            event.preventDefault();
            void load();
          }}
        >
          <div className="min-w-0">
            <TextField label="From" type="date" value={from} onChange={setFrom} max={to} />
          </div>
          <div className="min-w-0">
            <TextField label="To" type="date" value={to} onChange={setTo} min={from} max={todayIso()} />
          </div>
          <button type="submit" className="btn btn-primary" disabled={loading || rangeInvalid}>
            {loading ? 'Loading' : 'Run report'}
          </button>
          {rangeInvalid && (
            <p className="field-error self-center">The start of the range is after the end.</p>
          )}
        </form>
      </Card>

      {loading && !metrics ? (
        <Spinner label="Loading the report" />
      ) : !metrics ? (
        <Empty title="No report yet" body="Choose a date range above and run the report." />
      ) : (
        <>
          {/* --- Headline counts --------------------------------------------- */}
          <div className="mb-6 grid gap-4 sm:grid-cols-4">
            <StatTile label="Requested" value={metrics.bookingsRequested} />
            <StatTile label="Confirmed" value={metrics.bookingsConfirmed} />
            <StatTile label="Completed" value={metrics.bookingsCompleted} />
            <StatTile label="Cancelled" value={metrics.bookingsCancelled} />
          </div>

          {/* --- Money --------------------------------------------------------- */}
          <div className="mb-10 grid gap-4 sm:grid-cols-2">
            <Card className="p-5">
              <p className="field-label">Received</p>
              <p className="font-display text-4xl font-extrabold">
                {money(metrics.platformFeeCents > 0 ? metrics.netCents : metrics.receivedCents)}
              </p>
              <p className="mt-1 text-xs text-[color:var(--color-ink-soft)]">
                Money that actually arrived between {metrics.from} and {metrics.to}.
              </p>
              {metrics.platformFeeCents > 0 && (
                <p className="mt-2 text-xs text-[color:var(--color-ink-soft)]">
                  Your clients paid {money(metrics.receivedCents)}. Our 5% on card payments came
                  to {money(metrics.platformFeeCents)}, and goes away on Professional.
                </p>
              )}
            </Card>
            <Card className="p-5">
              <p className="field-label">Outstanding</p>
              <p className="font-display text-4xl font-extrabold">{money(metrics.outstandingCents)}</p>
              <p className="mt-1 text-xs text-[color:var(--color-ink-soft)]">
                Billed on bookings in this range but not yet paid.
              </p>
            </Card>
          </div>

          {/* --- Clients and pets ------------------------------------------- */}
          <div className="mb-10 grid gap-4 sm:grid-cols-2">
            <Card className="p-5">
              <p className="field-label">New clients</p>
              <p className="font-display text-4xl font-extrabold">{metrics.newClients}</p>
            </Card>
            <Card className="p-5">
              <p className="field-label">Active pets</p>
              <p className="font-display text-4xl font-extrabold">{metrics.activePets}</p>
              <p className="mt-1 text-xs text-[color:var(--color-ink-soft)]">Right now, not just this range.</p>
            </Card>
          </div>

          {/* --- Per-service breakdown ---------------------------------------- */}
          <section>
            <h2 className="mb-5 text-2xl">By service</h2>
            {metrics.byService.length === 0 ? (
              <Empty title="No revenue yet" body="Nothing was received in this date range." />
            ) : (
              <Card className="p-5">
                <ul className="space-y-4">
                  {metrics.byService.map((service) => (
                    <li key={service.serviceId} className="min-w-0">
                      <div className="mb-1.5 flex flex-wrap items-baseline justify-between gap-x-4 gap-y-0.5">
                        <span className="min-w-0 truncate font-semibold">{service.name}</span>
                        <span className="typed shrink-0 text-sm text-[color:var(--color-ink-soft)]">
                          {service.bookings} booking{service.bookings === 1 ? '' : 's'} &middot;{' '}
                          <span className="font-semibold text-[color:var(--color-ink)]">
                            {money(service.receivedCents)}
                          </span>
                        </span>
                      </div>
                      <div className="h-2.5 w-full overflow-hidden rounded-full bg-[color:var(--color-sheet)]">
                        <div
                          className="h-full rounded-full bg-[color:var(--color-stamp)]"
                          style={{
                            width: `${topServiceEarnings > 0 ? (service.receivedCents / topServiceEarnings) * 100 : 0}%`,
                          }}
                        />
                      </div>
                    </li>
                  ))}
                </ul>
              </Card>
            )}
          </section>
        </>
      )}
    </div>
  );
}

function StatTile({ label, value }: { label: string; value: number }) {
  return (
    <Card className="p-5">
      <p className="field-label">{label}</p>
      <p className="font-display text-3xl font-extrabold">{value}</p>
    </Card>
  );
}
